/**
 * Provider Messaging — the patient ↔ care-team inbox in core, measured the way
 * the Zendesk page measures tickets: volume, first response, who answers, and
 * who is still waiting. Data from /provider-messaging/report; the request and
 * response rules are in backend services/providerMessaging.
 */

import { useCallback, useEffect, useState } from 'react'
import { format } from 'date-fns'
import {
  Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react'
import { providerMessagingAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui'

const defaultRange = () => {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 29)
  return { from, to }
}

function formatMinutes(mins) {
  if (mins == null) return '—'
  if (mins < 60) return `${Math.round(mins)}m`
  if (mins < 1440) return `${(mins / 60).toFixed(1)}h`
  return `${(mins / 1440).toFixed(1)}d`
}
const formatHours = mins => (mins == null ? null : Number((mins / 60).toFixed(1)))
const pct = value => (value == null ? '—' : `${Math.round(value * 100)}%`)
const count = value => (value == null ? '—' : Math.round(value).toLocaleString())
const dayLabel = iso => format(new Date(`${iso}T12:00:00`), 'MMM d')
const hourLabel = hour => `${hour % 12 || 12}${hour < 12 ? 'a' : 'p'}`
const ROLE_LABELS = { physician: 'Physician', care_team: 'Care team' }

function Stat({ title, value, note }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {note && <p className="text-xs text-muted-foreground">{note}</p>}
      </CardContent>
    </Card>
  )
}

function ChartCard({ title, description, children, height = 'h-72' }) {
  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle>{description && <CardDescription>{description}</CardDescription>}</CardHeader>
      <CardContent><div className={height}><ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer></div></CardContent>
    </Card>
  )
}

const axis = { tick: { fontSize: 11 }, tickLine: false }
const tooltipStyle = { contentStyle: { background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', fontSize: 12 } }

export default function ProviderMessagingPage() {
  const { currentClinicId } = useAuthStore()
  const [dateRange, setDateRange] = useState(defaultRange)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!currentClinicId || !dateRange?.from || !dateRange?.to) return
    setLoading(true)
    setError(null)
    try {
      const response = await providerMessagingAPI.getReport({
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
      })
      setData(response.data.data)
    } catch (err) {
      setData(null)
      setError(err.response?.data?.error?.message || 'Could not load provider messaging.')
    } finally {
      setLoading(false)
    }
  }, [currentClinicId, dateRange])

  useEffect(() => { load() }, [load])

  const s = data?.summary
  const staffSince = s?.staff_since ? new Date(s.staff_since) : null
  const daily = (data?.daily || []).map(d => ({ ...d, label: dayLabel(d.date), median_hours: formatHours(d.median_minutes) }))
  const weekly = (data?.weekly || []).map(w => ({ ...w, label: `${dayLabel(w.week_start)}${w.partial ? '*' : ''}`, median_hours: formatHours(w.median_minutes), p90_hours: formatHours(w.p90_minutes) }))
  const byHour = (data?.by_hour || []).map(h => ({ ...h, label: hourLabel(h.hour), median_hours: formatHours(h.median_minutes) }))

  return (
    <div className="container mx-auto space-y-6 px-4 py-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Provider Messaging</h1>
          <p className="mt-1 text-muted-foreground">Patient ↔ care-team messages in our own inbox: volume, first response, who answers, who is waiting.</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="outline" size="sm" disabled={loading || !currentClinicId} onClick={load} aria-label="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>

      {!currentClinicId ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">Select a clinic: each clinic has its own patient inbox.</CardContent></Card>
      ) : error ? (
        <div role="alert" className="flex items-center gap-2 rounded-md border p-4 text-sm text-destructive"><AlertCircle className="h-4 w-4" />{error}</div>
      ) : loading && !data ? (
        <div role="status" className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Loading messages…</div>
      ) : data && (s.patient_messages + s.staff_messages === 0 && s.open_now === 0) ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">No provider messages for this clinic in these dates.</CardContent></Card>
      ) : data && <>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <Stat title="Patient messages" value={count(s.patient_messages)} note={`${count(s.requests)} requests · ${count(s.patients)} patients`} />
          <Stat title="Staff messages" value={count(s.staff_messages)} note={`${count(s.physician_messages)} physician · ${count(s.outreach_messages)} outreach`} />
          <Stat title="Median first response" value={formatMinutes(s.median_minutes)} note={`${count(s.answered)} answered${s.marked_done ? ` · ${count(s.marked_done)} marked done` : ''}`} />
          <Stat title="P90 first response" value={formatMinutes(s.p90_minutes)} note={`${pct(s.within_24h)} within 24h`} />
          <Stat title="Waiting now" value={count(s.open_since_staff)}
            note={s.open_now > s.open_since_staff ? `+${count(s.open_now - s.open_since_staff)} from before ${staffSince ? format(staffSince, 'MMM d') : ''}` : s.oldest_open_minutes != null ? `oldest ${formatMinutes(s.oldest_open_minutes)}` : 'none'} />
          <Stat title="Read by patient" value={pct(s.read_rate)} note="of staff messages" />
        </div>

        {data.marked_done_read === false && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Couldn't read what staff marked done in the EHR, so those conversations still count as waiting.
          </p>
        )}

        {staffSince && staffSince > new Date(`${data.window.startDate}T00:00:00`) && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Staff first replied in this inbox on {format(staffSince, 'MMM d, yyyy')}. Patient messages from before then were answered elsewhere, so their response times read long and they stay open here.
          </p>
        )}

        <ChartCard title="Message Volume" description="Daily patient and staff messages, with the median first response of requests opened that day">
          <ComposedChart data={daily}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="label" {...axis} />
            <YAxis yAxisId="n" {...axis} />
            <YAxis yAxisId="h" orientation="right" {...axis} unit="h" />
            <Tooltip {...tooltipStyle} />
            <Legend />
            <Bar yAxisId="n" dataKey="patient_messages" name="Patient" fill="#3B82F6" radius={[2, 2, 0, 0]} />
            <Bar yAxisId="n" dataKey="staff_messages" name="Staff" fill="#22C55E" radius={[2, 2, 0, 0]} />
            <Line yAxisId="h" dataKey="median_hours" name="Median response (h)" stroke="#F59E0B" dot={false} connectNulls />
          </ComposedChart>
        </ChartCard>

        <div>
          <h2 className="text-lg font-semibold text-foreground">Week over Week</h2>
          <p className="text-sm text-muted-foreground">The 12 weeks to the end of the selected dates, Monday to Sunday ET{weekly.some(w => w.partial) ? '; * marks a week still in progress' : ''}</p>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Requests by Week">
            <BarChart data={weekly}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="label" {...axis} />
              <YAxis {...axis} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="requests" name="Requests" fill="#3B82F6" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ChartCard>
          <ChartCard title="First Response by Week" description="Median and P90, hours">
            <ComposedChart data={weekly}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="label" {...axis} />
              <YAxis {...axis} unit="h" />
              <Tooltip {...tooltipStyle} />
              <Legend />
              <Line dataKey="median_hours" name="Median" stroke="#22C55E" connectNulls />
              <Line dataKey="p90_hours" name="P90" stroke="#EF4444" connectNulls />
            </ComposedChart>
          </ChartCard>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Response Times" description="Requests opened in these dates, by time to first staff reply">
            <BarChart data={data.response_buckets}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="label" {...axis} />
              <YAxis {...axis} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="count" name="Requests" fill="#8B5CF6" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ChartCard>
          <ChartCard title="When Patients Write" description="Patient messages by hour (ET), and the median wait of requests opened in that hour">
            <ComposedChart data={byHour}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="label" {...axis} interval={1} />
              <YAxis yAxisId="n" {...axis} />
              <YAxis yAxisId="h" orientation="right" {...axis} unit="h" />
              <Tooltip {...tooltipStyle} />
              <Legend />
              <Bar yAxisId="n" dataKey="patient_messages" name="Patient messages" fill="#3B82F6" radius={[2, 2, 0, 0]} />
              <Line yAxisId="h" dataKey="median_hours" name="Median wait (h)" stroke="#F59E0B" dot={false} connectNulls />
            </ComposedChart>
          </ChartCard>
        </div>

        {!data.staff_hidden && <Card>
          <CardHeader><CardTitle>Responders</CardTitle><CardDescription>Everyone who wrote to a patient in these dates. A response is the first staff message after a patient&apos;s; outreach is a message that answered none.</CardDescription></CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs">
                  <tr>
                    <th className="p-3 text-left font-medium">Staff</th><th className="p-3 text-left font-medium">Role</th>
                    <th className="p-3 text-right font-medium">Messages</th><th className="p-3 text-right font-medium">Responses</th>
                    <th className="p-3 text-right font-medium">Outreach</th><th className="p-3 text-right font-medium">Patients</th>
                    <th className="p-3 text-right font-medium">Median</th><th className="p-3 text-right font-medium">P90</th>
                  </tr>
                </thead>
                <tbody>
                  {data.responders.map(r => (
                    <tr key={r.email} className="border-t">
                      <td className="p-3">{r.name || r.email}{r.name && <span className="block text-xs text-muted-foreground">{r.email}</span>}</td>
                      <td className="p-3"><Badge variant={r.role === 'physician' ? 'default' : 'secondary'}>{ROLE_LABELS[r.role] || r.role}</Badge></td>
                      <td className="p-3 text-right font-mono">{count(r.messages)}</td>
                      <td className="p-3 text-right font-mono">{count(r.responses)}</td>
                      <td className="p-3 text-right font-mono">{count(r.outreach)}</td>
                      <td className="p-3 text-right font-mono">{count(r.patients)}</td>
                      <td className="p-3 text-right font-mono">{formatMinutes(r.median_minutes)}</td>
                      <td className="p-3 text-right font-mono">{formatMinutes(r.p90_minutes)}</td>
                    </tr>
                  ))}
                  {!data.responders.length && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No staff messages in these dates.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>}

        <Card>
          <CardHeader>
            <CardTitle>Waiting for a Reply</CardTitle>
            <CardDescription>Patients whose last message has no staff reply yet, regardless of the dates above. Oldest first{s.open_now > data.open.length ? `; the first ${data.open.length} of ${count(s.open_now)}` : ''}.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs">
                  <tr>
                    <th className="p-3 text-left font-medium">Patient</th><th className="p-3 text-left font-medium">Waiting since</th>
                    <th className="p-3 text-right font-medium">Waiting</th><th className="p-3 text-right font-medium">Messages</th>
                    {!data.staff_hidden && <th className="p-3 text-left font-medium">Last staff reply from</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.open.map(o => (
                    <tr key={o.patient_id} className={`border-t ${o.before_staff ? 'text-muted-foreground' : ''}`}>
                      <td className="p-3">{o.patient_name || 'Unknown patient'}<span className="block font-mono text-xs text-muted-foreground">{o.patient_id}</span></td>
                      <td className="p-3">{format(new Date(o.waiting_since), 'MMM d, h:mm a')}{o.before_staff && <span className="block text-xs">before staff replied here</span>}</td>
                      <td className="p-3 text-right font-mono">{formatMinutes(o.waiting_minutes)}</td>
                      <td className="p-3 text-right font-mono">{o.messages}</td>
                      {!data.staff_hidden && <td className="p-3 text-xs">{o.last_staff || '—'}</td>}
                    </tr>
                  ))}
                  {!data.open.length && <tr><td colSpan={data.staff_hidden ? 4 : 5} className="p-6 text-center text-muted-foreground">Nobody is waiting.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          <li>A request is a run of patient messages with no staff message between them; it opens at the first and is answered by the next staff message. Times are calendar time — nothing records the team&apos;s hours.</li>
          <li>Physician is anyone in the clinic&apos;s physicians list; care team is any other staff sender. A sender in only one thread who is not the patient (a caregiver, an old address) counts as the patient.</li>
          <li>Read by patient: staff messages the patient has opened. Dates are Eastern.</li>
        </ul>
      </>}
    </div>
  )
}
